import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Table, Alert, Pagination, Space, Button, Result, Skeleton } from 'antd'
import { getDataOfType } from 'src/lib/utils/get-data-of-type'
import B2BLayout from 'src/react-app/layouts/b2b'
import fetchAPI, { IHttpError } from 'src/lib/utils/fetch-api'
import { formDataToUrlParams } from 'src/react-app/pages/list-orders/utils'
import { calcTotalWidth } from 'src/lib/utils/list'
import { TableWrapper, PaginationWrapper } from './styles'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import dayjs from 'dayjs'
import OrderFilter from './orders-filter'
import './index.css'
import { FileExcelOutlined } from "@ant-design/icons"
import { ResultStatusType } from 'antd/lib/result'

const createViewed = createSelector(
  (state: any) => state.viewed,
  counts => counts
)

function downloadUrl(url, exportName) {
  const downloadAnchorNode = document.createElement('a')
  downloadAnchorNode.setAttribute('href', url)
  downloadAnchorNode.setAttribute('download', exportName)
  document.body.appendChild(downloadAnchorNode) // required for firefox
  downloadAnchorNode.click()
  downloadAnchorNode.remove()
}

interface IError {
  code?: ResultStatusType,
  message?: string,
}

export default function AdminObjectsListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const orderFiltersRef = useRef<any>({})
  const [dataSource, setDataSource] = useState<any[]>([])
  const [isDataLoading, setIsDataLoading] = useState(true)
  const [totalRows, setTotalRows] = useState<number>(0)
  const [pageErrors, setPageErrors] = useState<String[]>([])
  const [page, setPage] = useState<number>(1)
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const { counts } = useSelector(createViewed)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<IError | null>()

  const breadcrumbs = useMemo(() => (
    [{
      href: '/reports',
      title: 'Отчеты',
    }, {
      href: '/report/marketing-list',
      title: 'Маркетинг',
    }]
  ), [location.pathname])
  //const { sessionData } = useSelector(createSession)

  const onButtonCreate = useCallback(() => navigate(`/claims/new`), [history])

  const addDataColumns = useMemo(() => {
    const addDataColumns: any[] = [
      {
        title: 'ID',
        width: 100,
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: 'Город',
        width: 200,
        dataIndex: 'city',
        key: 'city',
      },
      {
        title: 'Организация',
        width: 150,
        dataIndex: 'organization-title',
        key: 'organization-title',
      },
      {
        title: 'Менеджер',
        width: 150,
        dataIndex: 'manager-name',
        key: 'manager-name',
      },
      {
        title: 'ID обращения',
        width: 150,
        dataIndex: 'parent-id',
        key: 'parent-id',
      },
      {
        title: 'Дата создания обращения',
        width: 150,
        dataIndex: 'case-date-fact',
        key: 'case-date-fact',
        render: val => val && dayjs(val).format('DD.MM.YYYY')
      },
      {
        title: 'ФИО Клиента',
        width: 150,
        dataIndex: 'client-name',
        key: 'client-name',
      },
      {
        title: 'Телефон клиента',
        width: 150,
        dataIndex: 'client-phone',
        key: 'client-phone',
      },
      {
        title: 'Стадия заказа',
        width: 150,
        dataIndex: 'stage-code',
        key: 'stage-code',
      },
      {
        title: 'Причина закрытия заказа',
        width: 150,
        dataIndex: 'cancel-title',
        key: 'cancel-title',
      },
      {
        title: 'Дата консультации',
        width: 150,
        dataIndex: 'order-date-fact',
        key: 'order-date-fact',
        render: val => val && dayjs(val).format('DD.MM.YYYY')
      },
      {
        title: 'Отв. консультации',
        width: 150,
        dataIndex: 'order-responsiples',
        key: 'order-responsiples',
      },
      {
        title: 'Дата замера',
        width: 150,
        dataIndex: 'measurement-date-fact',
        key: 'measurement-date-fact',
        render: val => val && dayjs(val).format('DD.MM.YYYY')
      },
      {
        title: 'Отв. замера',
        width: 150,
        dataIndex: 'measurement-responsiples',
        key: 'measurement-responsiples',
      },
      {
        title: 'Дата оплаты',
        width: 150,
        dataIndex: 'manufacture-date-fact',
        key: 'manufacture-date-fact',
        render: val => val && dayjs(val).format('DD.MM.YYYY')
      },
      {
        title: 'Отв. производства',
        width: 150,
        dataIndex: 'manufacture-responsiples',
        key: 'manufacture-responsiples',
      },
      {
        title: 'Дата доставки',
        width: 150,
        dataIndex: 'delivery-date-fact',
        key: 'delivery-date-fact',
        render: val => val && dayjs(val).format('DD.MM.YYYY')
      },
      {
        title: 'Отв. доставки',
        width: 150,
        dataIndex: 'delivery-responsiples',
        key: 'delivery-responsiples',
      },
      {
        title: 'Дата монтажа',
        width: 150,
        dataIndex: 'mounting-date-fact',
        key: 'mounting-date-fact',
        render: val => val && dayjs(val).format('DD.MM.YYYY')
      },
      {
        title: 'Отв. монтажа',
        width: 150,
        dataIndex: 'mounting-responsiples',
        key: 'mounting-responsiples',
      },
      {
        title: 'Дата аудита',
        width: 150,
        dataIndex: 'audit-date-fact',
        key: 'audit-date-fact',
        render: val => val && dayjs(val).format('DD.MM.YYYY')
      },
      {
        title: 'Отв. аудита',
        width: 150,
        dataIndex: 'audit-responsiples',
        key: 'audit-responsiples',
      },
      {
        title: 'Номер производственного заказа',
        width: 150,
        dataIndex: 'production-order-number',
        key: 'production-order-number',
      },
      {
        title: 'Сумма заказа',
        width: 150,
        dataIndex: 'contract-sum',
        key: 'contract-sum',
      },
      {
        title: 'Стоимость изделий',
        width: 150,
        dataIndex: 'products-price',
        key: 'products-price',
      },
      {
        title: 'Монтаж',
        width: 150,
        dataIndex: 'mounting',
        key: 'mounting',
      },
      {
        title: 'Доп. услуги',
        width: 150,
        dataIndex: 'demounting',
        key: 'demounting',
      },
      {
        title: 'Доставка',
        width: 150,
        dataIndex: 'delivery',
        key: 'delivery',
      },
      {
        title: 'Вывоз мусора',
        width: 150,
        dataIndex: 'garbage-remove',
        key: 'garbage-remove',
      },
      {
        title: 'Агентское вознаграждение',
        width: 150,
        dataIndex: 'agent-commission',
        key: 'agent-commission',
      },
      {
        title: 'Габариты (м2)',
        width: 150,
        dataIndex: 'square',
        key: 'square',
      },
      {
        title: 'Признак заказа',
        width: 150,
        dataIndex: 'order-attribute',
        key: 'order-attribute',
      },
      {
        title: 'Вид контакта',
        width: 150,
        dataIndex: 'source-appeal',
        key: 'source-appeal',
      },
      {
        title: 'Кампания',
        width: 150,
        dataIndex: 'campaign',
        key: 'campaign',
      },
      {
        title: 'Яндекс Метрика ID',
        width: 150,
        dataIndex: 'yaClientId',
        key: 'yaClientId',
      },
      {
        title: 'Источник',
        width: 150,
        dataIndex: 'source',
        key: 'source',
      },
      {
        title: 'Канал',
        width: 150,
        dataIndex: 'medium',
        key: 'medium',
      },
      {
        title: 'Содержание объявления',
        width: 150,
        dataIndex: 'content',
        key: 'content',
      },
      {
        title: 'Фраза|запрос',
        width: 150,
        dataIndex: 'term',
        key: 'term',
      },
    ]
    return addDataColumns
  }, [])

  const totalWidth: any = useMemo(() => calcTotalWidth( addDataColumns), [ addDataColumns])

  // @ts-ignore
  const finalColumns = useMemo(() => [].concat(addDataColumns).filter(item => item.title), [addDataColumns])
  const cbTableRowClick = useCallback((item) => item['id'] && navigate(`/claims/${item['id']}`), [history])

  const deleteRecord = useCallback(async (currentId) => {
    try{
      await fetchAPI(`/api/collections/objects/${currentId}`, { method: 'DELETE'})
      setDataSource(dataSource => dataSource.filter(({ id }) => id !== currentId))
    }catch(error){
      console.error(error)
    }
  }, [])

  const getData = useCallback(async (value, addUrlParams?: string) => {
    setLoading(true)
    const urlParams = formDataToUrlParams(value)
    const pageErrors: string[] = []
    await fetchAPI(`/api/order_report/list?${urlParams}&${addUrlParams}`)
      .then(collections => {
        const dataSource: any[] = collections?.data?.data || []
        const newDataSource = dataSource.map((item) => ({
          ...item,
          deleteRecord,
        }))

        const totalRows = collections?.data?.['total-rows'] || 0
        setTotalRows(totalRows)
        setDataSource(newDataSource)
        setPageErrors(pageErrors)
      })
      .catch(e => {
        console.error({ e })
        setError({
          message: (e as IHttpError)?.message || 'Неизвестная ошибка',
          code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType
        })
      })
      .finally(() => {
        setIsDataLoading(false)
        setLoading(false)
      })
  }, [deleteRecord])

  const onPagination = useCallback(async ( page, pageSize) => {
    setPage(page)
    const urlParams = `page=${page}&pageSize=${pageSize}`
    setIsDataLoading(true)
    await getData(orderFiltersRef.current, urlParams)
    setIsDataLoading(false)
  }, [getData])

  useEffect(() => {
    const urlParams = `page=${page}&pageSize=10`
    page === 1 && getData(orderFiltersRef.current,urlParams)
  }, [counts, page, getData])

  const rowClassName = useCallback(record => {
    const isColor = record['orders-quantity'] >= 2 && record['orders-quantity-new'] >= 1
    return isColor ? 'table-client-row-dark' :  'table-client-row-light'
  }, [])

  const onOrderFilter = useCallback(value => {
    Object.assign(orderFiltersRef.current, value)
    getData(value)
  }, [getData])

  const onPrint = useCallback(async () => {
    const urlParams = formDataToUrlParams(orderFiltersRef.current)

    downloadUrl(`/api/order_report/list/excel?${urlParams}`, `Отчет - маркетинг.xlsx`)
  }, [])

  const onShow = useCallback(async () => {
    setShowMoreFilters(prevState => !prevState)
  }, [])

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {loading && <Skeleton active/>}
      {!loading && error &&
        <Result status={error.code} title='Ошибка' subTitle={error.message} />
      }
      {!loading && !error &&
        <>
          <Space direction='horizontal'>
            <Button style={{ marginBottom: 16 }} onClick={onPrint}>
              <FileExcelOutlined /> Экспорт в эксель
            </Button>
          </Space>
          <div className={'filter-wrapper ' + (showMoreFilters ? 'active' : '')}>
            <Space align="baseline">
              <OrderFilter onChange={onOrderFilter} />
            </Space>
          </div>
          <Button style={{ marginBottom: 16 }} onClick={onShow}>
            Показать {showMoreFilters ? 'меньше' : 'больше'} фильтров
          </Button>
          {pageErrors.length > 0 ? (
            <div style={{ marginBottom: 40 }}>
              <Alert
                message="При выполнении операции возникла ошибка:"
                showIcon
                type="error"
                description={pageErrors.join('. ')}
              />
            </div>
          ) : null}
          <TableWrapper>
            <Table
              columns={finalColumns}
              rowClassName={rowClassName}
              size="small"
              dataSource={dataSource}
              scroll={{ x: totalWidth, y: '72vh' }}
              pagination={false}
              loading={isDataLoading}
              onHeaderRow={(column: any) => ({
                style: {
                  fontSize: column.key && column.key.substr(-3) === '-at' ? 9 : 13,
                },
              })}
            />
          </TableWrapper>
          {Boolean(totalRows) && <PaginationWrapper>
            <Pagination defaultCurrent={1} pageSize={10} total={totalRows} onChange={onPagination} showSizeChanger={false} />
          </PaginationWrapper>}
        </>
      }
    </B2BLayout>
  )
}
